'use client';

import { TechnologyOption } from '@/lib/tech-stack-types';
import {
  prepareMarketShareData,
  prepareMarketShareByLicense,
  prepareMarketShareByMaturity,
  getCategoryDisplayName,
} from '@/lib/visualization-data';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useState } from 'react';

interface TechStackMarketShareProps {
  technologies: TechnologyOption[];
}

export default function TechStackMarketShare({ technologies }: TechStackMarketShareProps) {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'category' | 'license' | 'maturity'>('category');

  const categoryData = prepareMarketShareData(technologies);
  const licenseData = prepareMarketShareByLicense(technologies);
  const maturityData = prepareMarketShareByMaturity(technologies);

  const categories = ['all', ...categoryData.map(d => d.category)];

  // Get data for current view
  const getCurrentData = () => {
    if (viewMode === 'license') return licenseData;
    if (viewMode === 'maturity') return maturityData;
    if (selectedCategory === 'all') {
      // Aggregate all technologies
      const allTechs = categoryData.flatMap(c => c.technologies);
      const total = allTechs.reduce((sum, t) => sum + t.value, 0);
      return {
        category: 'All Categories',
        technologies: allTechs
          .sort((a, b) => b.value - a.value)
          .slice(0, 10) // Top 10 technologies
          .map(t => ({
            ...t,
            percentage: (t.value / total) * 100,
          })),
      };
    }
    return categoryData.find(d => d.category === selectedCategory) || categoryData[0];
  };

  const currentData = getCurrentData();

  // Custom label for pie chart
  const renderLabel = (entry: any) => {
    if (entry.percentage < 5) return null; // Don't show label for small slices
    return `${entry.percentage.toFixed(1)}%`;
  };

  // Custom label component with proper styling
  const CustomLabel = (props: any) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, percentage } = props;
    if (percentage < 5) return null;

    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="#E8D5B7"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize="14"
        fontWeight="600"
      >
        {`${percentage.toFixed(1)}%`}
      </text>
    );
  };

  return (
    <div className="space-y-6">
      {/* Title and Description */}
      <div>
        <h3 className="text-2xl font-bold text-foreground mb-2">Market Share Analysis</h3>
        <p className="text-foreground-secondary text-sm">
          Technology distribution based on community size and popularity trends.
          Larger slices indicate higher adoption and market presence.
        </p>
      </div>

      {/* View Mode Selector */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setViewMode('category')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            viewMode === 'category'
              ? 'bg-accent text-accent-foreground'
              : 'bg-surface text-foreground-secondary hover:bg-surface/80'
          }`}
        >
          By Category
        </button>
        <button
          onClick={() => setViewMode('license')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            viewMode === 'license'
              ? 'bg-accent text-accent-foreground'
              : 'bg-surface text-foreground-secondary hover:bg-surface/80'
          }`}
        >
          By License Type
        </button>
        <button
          onClick={() => setViewMode('maturity')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            viewMode === 'maturity'
              ? 'bg-accent text-accent-foreground'
              : 'bg-surface text-foreground-secondary hover:bg-surface/80'
          }`}
        >
          By Maturity Level
        </button>
      </div>

      {/* Category Selector (only for category view) */}
      {viewMode === 'category' && (
        <div>
          <label className="text-sm font-semibold text-foreground-secondary mb-2 block">
            Select Category
          </label>
          <div className="flex flex-wrap gap-2">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3 py-1.5 rounded text-sm transition-colors ${
                  selectedCategory === category
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-surface text-foreground-secondary hover:bg-surface/80'
                }`}
              >
                {category === 'all' ? 'All Categories' : getCategoryDisplayName(category)}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Pie Chart */}
        <div className="bg-surface rounded-lg p-6">
          <h4 className="text-lg font-semibold text-foreground mb-4">
            {currentData.category === 'All Categories'
              ? 'Top 10 Technologies (Overall)'
              : getCategoryDisplayName(currentData.category)}
          </h4>
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={currentData.technologies}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={CustomLabel}
                outerRadius={120}
                innerRadius={60}
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
              >
                {currentData.technologies.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#0A1E2E',
                  border: '1px solid #E8D5B7',
                  borderRadius: '8px',
                  color: '#E8D5B7',
                }}
                formatter={(value: number | undefined, name: string | undefined, props: any) => [
                  `${props.payload.percentage.toFixed(1)}%`,
                  name ?? '',
                ]}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Technology List */}
        <div className="bg-surface rounded-lg p-6">
          <h4 className="text-lg font-semibold text-foreground mb-4">Technology Breakdown</h4>
          <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
            {currentData.technologies.map((tech, index) => (
              <div key={tech.name} className="flex items-center gap-3">
                <div className="flex-shrink-0 w-6 text-center text-sm font-semibold text-foreground-tertiary">
                  {index + 1}
                </div>
                <div
                  className="flex-shrink-0 w-4 h-4 rounded"
                  style={{ backgroundColor: tech.color }}
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground truncate">{tech.name}</div>
                </div>
                <div className="flex-shrink-0 text-right">
                  <div className="text-sm font-semibold text-accent">
                    {tech.percentage.toFixed(1)}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Insights */}
      <div className="bg-surface rounded-lg p-6">
        <h4 className="text-lg font-semibold text-foreground mb-3">Market Insights</h4>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-background rounded-lg p-4">
            <div className="text-sm font-semibold text-foreground-secondary mb-1">Top Technology</div>
            <div className="text-xl font-bold text-accent">
              {currentData.technologies[0]?.name || 'N/A'}
            </div>
            <div className="text-sm text-foreground-tertiary mt-1">
              {currentData.technologies[0]?.percentage.toFixed(1)}% market share
            </div>
          </div>

          <div className="bg-background rounded-lg p-4">
            <div className="text-sm font-semibold text-foreground-secondary mb-1">Total Technologies</div>
            <div className="text-xl font-bold text-accent">
              {currentData.technologies.length}
            </div>
            <div className="text-sm text-foreground-tertiary mt-1">
              {viewMode === 'category' && selectedCategory !== 'all'
                ? `in ${getCategoryDisplayName(currentData.category)}`
                : viewMode === 'license'
                ? 'across license types'
                : viewMode === 'maturity'
                ? 'across maturity levels'
                : 'shown (top 10)'}
            </div>
          </div>

          <div className="bg-background rounded-lg p-4">
            <div className="text-sm font-semibold text-foreground-secondary mb-1">
              {viewMode === 'license' ? 'Dominant License' :
               viewMode === 'maturity' ? 'Dominant Maturity' : 'Market Leader'}
            </div>
            <div className="text-lg font-semibold text-foreground">
              {currentData.technologies[0]?.name || 'N/A'}
            </div>
            <div className="text-sm text-foreground-tertiary mt-1">
              Leading in this segment
            </div>
          </div>

          <div className="bg-background rounded-lg p-4">
            <div className="text-sm font-semibold text-foreground-secondary mb-1">Market Concentration</div>
            <div className="text-lg font-semibold text-foreground">
              {currentData.technologies.length > 0
                ? currentData.technologies.slice(0, 3).reduce((sum, t) => sum + t.percentage, 0).toFixed(1)
                : '0'}%
            </div>
            <div className="text-sm text-foreground-tertiary mt-1">
              Held by top 3 technologies
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="bg-surface/50 border border-border rounded-lg p-4">
        <p className="text-xs text-foreground-tertiary">
          <strong className="text-foreground-secondary">Note:</strong> Market share is calculated based on community size and popularity trends.
          Real-time usage data integration coming soon.
        </p>
      </div>
    </div>
  );
}
